import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users,
  Shield,
  Heart,
  MessageCircle,
  Flag,
  AlertTriangle,
  CheckCircle,
  Award,
  Ban,
  Smile,
  ThumbsUp,
  Mail,
  ChevronDown,
  ChevronUp,
  Star,
  Globe,
  Clock,
  BookOpen
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const Guidelines = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const coreValues = [
    {
      icon: <Heart className="w-8 h-8 text-[#00fff5]" />,
      title: 'Respect',
      desc: 'Treat every player with dignity and kindness'
    },
    {
      icon: <Shield className="w-8 h-8 text-[#00fff5]" />,
      title: 'Integrity',
      desc: 'Play fair and compete honestly'
    },
    {
      icon: <Users className="w-8 h-8 text-[#00fff5]" />,
      title: 'Community',
      desc: 'Build a supportive gaming environment'
    },
    {
      icon: <Smile className="w-8 h-8 text-[#00fff5]" />,
      title: 'Positivity',
      desc: 'Spread good vibes and encouragement'
    }
  ];

  const dos = [
    {
      icon: <ThumbsUp className="w-6 h-6 text-[#00E676]" />,
      title: 'Be Respectful',
      desc: 'Treat all players with courtesy, regardless of their skill level'
    },
    {
      icon: <MessageCircle className="w-6 h-6 text-[#00E676]" />,
      title: 'Communicate Positively',
      desc: 'Use constructive language and help teammates improve'
    },
    {
      icon: <Shield className="w-6 h-6 text-[#00E676]" />,
      title: 'Report Issues',
      desc: 'Flag inappropriate behavior to moderators'
    },
    {
      icon: <Award className="w-6 h-6 text-[#00E676]" />,
      title: 'Celebrate Others',
      desc: 'Appreciate good plays and sportsmanship'
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-[#00E676]" />,
      title: 'Follow Rules',
      desc: 'Adhere to tournament guidelines and fair play policies'
    }
  ];

  const donts = [
    {
      icon: <Ban className="w-6 h-6 text-[#EF4444]" />,
      title: 'No Toxic Behavior',
      desc: 'Avoid harassment, bullying, or hate speech'
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-[#EF4444]" />,
      title: 'No Cheating',
      desc: 'Never use hacks, aimbots, or unauthorized tools'
    },
    {
      icon: <Flag className="w-6 h-6 text-[#EF4444]" />,
      title: 'No Spamming',
      desc: 'Don\'t flood chat with repetitive messages'
    },
    {
      icon: <Users className="w-6 h-6 text-[#EF4444]" />,
      title: 'No Account Sharing',
      desc: 'Each account is for individual use only'
    },
    {
      icon: <Globe className="w-6 h-6 text-[#EF4444]" />,
      title: 'No Personal Info',
      desc: 'Never share personal information of others'
    }
  ];

  const categories = [
    {
      id: 'respect',
      title: 'Respectful Communication',
      icon: <MessageCircle className="w-6 h-6 text-[#00fff5]" />,
      rules: [
        'Use polite and constructive language at all times',
        'No offensive, abusive, or discriminatory remarks',
        'Avoid personal attacks or targeted harassment',
        'Respect cultural differences and diverse backgrounds',
        'Keep conversations focused on the game and tournament'
      ]
    },
    {
      id: 'fairplay',
      title: 'Fair Play & Competition',
      icon: <Shield className="w-6 h-6 text-[#00fff5]" />,
      rules: [
        'Compete honestly without cheating or exploiting bugs',
        'No teaming in solo tournaments',
        'Report any suspected cheating to moderators',
        'Accept match results gracefully, win or lose',
        'Help maintain the integrity of the game'
      ]
    },
    {
      id: 'sportsmanship',
      title: 'Good Sportsmanship',
      icon: <Award className="w-6 h-6 text-[#00fff5]" />,
      rules: [
        'Congratulate opponents on good plays',
        'Avoid gloating or taunting after victories',
        'Encourage teammates who are struggling',
        'Learn from losses instead of making excuses',
        'Be humble in victory and gracious in defeat'
      ]
    },
    {
      id: 'chat',
      title: 'Chat & Communication',
      icon: <MessageCircle className="w-6 h-6 text-[#00fff5]" />,
      rules: [
        'Keep chat relevant to the game and tournament',
        'No spamming or excessive capitalization',
        'Avoid sharing inappropriate or offensive content',
        'Don\'t advertise other platforms or services',
        'Use team chat for coordination, not arguments'
      ]
    },
    {
      id: 'reporting',
      title: 'Reporting Misconduct',
      icon: <Flag className="w-6 h-6 text-[#00fff5]" />,
      rules: [
        'Use the in-app report feature for violations',
        'Provide clear evidence when reporting',
        'Don\'t falsely report other players',
        'Allow moderators time to investigate',
        'All reports are kept confidential'
      ]
    }
  ];

  const consequences = [
    { level: 'Minor First Offense', action: 'Warning', color: '#F59E0B' },
    { level: 'Repeated Minor Offenses', action: 'Temporary Chat/Match Ban', color: '#EF4444' },
    { level: 'Major Violations', action: 'Temporary Account Suspension', color: '#EF4444' },
    { level: 'Severe or Repeated Major', action: 'Permanent Account Ban', color: '#7F1D1D' },
    { level: 'Cheating/Hacking', action: 'Immediate Permanent Ban', color: '#7F1D1D' },
  ];

  const stats = [
    { value: '2K+', label: 'Community Members', icon: Users },
    { value: '24/7', label: 'Moderation', icon: Clock },
    { value: '95%', label: 'Positive Feedback', icon: ThumbsUp },
    { value: '< 1h', label: 'Report Response', icon: Flag },
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      <Navbar />
      
      {/* Hero Section with Animated Background */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#00fff5] rounded-full opacity-5 animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
              <Users className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-rajdhani font-bold text-white mb-6">
              Community <span className="text-[#00fff5]">Guidelines</span>
            </h1>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto mb-8">
              Building a positive, respectful, and fair gaming community for all players. 
              These guidelines help us maintain a safe environment where everyone can enjoy competitive gaming.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">BE RESPECTFUL</span>
              </div>
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">PLAY FAIR</span>
              </div>
              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-full px-6 py-2">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">STAY POSITIVE</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-6 text-center group hover:border-[#00fff5] transition"
                >
                  <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-3 group-hover:bg-[#00fff520] transition">
                    <IconComponent className="w-6 h-6 text-[#00fff5]" />
                  </div>
                  <div className="text-2xl md:text-3xl font-rajdhani font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400 font-rajdhani">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Our Core <span className="text-[#00fff5]">Values</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              The foundation of TheArenaX community
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-8 text-center group hover:border-[#00fff5] transition"
              >
                <div className="inline-block p-4 bg-[#00fff510] rounded-xl mb-4 group-hover:bg-[#00fff520] transition">
                  {value.icon}
                </div>
                <h3 className="text-xl font-rajdhani font-bold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 font-rajdhani text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dos and Don'ts */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Do's Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-gradient-to-b from-[#00E67620] to-transparent rounded-2xl p-8 border border-[#00E676]">
                <div className="flex items-center gap-3 mb-8">
                  <div className="p-3 bg-[#00E67620] rounded-xl">
                    <CheckCircle className="w-8 h-8 text-[#00E676]" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-rajdhani font-bold text-white">
                    <span className="text-[#00E676]">Do's</span> of TheArenaX
                  </h2>
                </div>

                <div className="space-y-6">
                  {dos.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-[#00E67620] rounded-lg flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="text-white font-rajdhani font-bold mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Don'ts Column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-gradient-to-b from-[#EF444420] to-transparent rounded-2xl p-8 border border-[#EF4444]">
                <div className="flex items-center gap-3 mb-8">
                  <div className="p-3 bg-[#EF444420] rounded-xl">
                    <Ban className="w-8 h-8 text-[#EF4444]" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-rajdhani font-bold text-white">
                    <span className="text-[#EF4444]">Don'ts</span> of TheArenaX
                  </h2>
                </div>

                <div className="space-y-6">
                  {donts.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-[#EF444420] rounded-lg flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="text-white font-rajdhani font-bold mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Detailed Guidelines */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block p-3 bg-[#00fff510] rounded-xl mb-4">
              <BookOpen className="w-8 h-8 text-[#00fff5]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Detailed <span className="text-[#00fff5]">Guidelines</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              Click on each section to learn more about our community standards
            </p>
          </motion.div>

          <div className="space-y-4">
            {categories.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-[#1E2338] border border-[#2A2F45] rounded-xl overflow-hidden hover:border-[#00fff5] transition"
              >
                <button
                  onClick={() => toggleSection(category.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-[#2A2F45] transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#00fff510] rounded-lg">
                      {category.icon}
                    </div>
                    <span className="text-white font-rajdhani font-bold text-lg">{category.title}</span>
                  </div>
                  {expandedSections.includes(category.id) ? (
                    <ChevronUp className="w-5 h-5 text-[#00fff5]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSections.includes(category.id) && (
                  <div className="px-6 pb-6">
                    <div className="h-px bg-[#2A2F45] mb-4"></div>
                    <ul className="space-y-3">
                      {category.rules.map((rule, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 bg-[#00fff5] rounded-full mt-2"></div>
                          <span className="text-gray-400 font-rajdhani flex-1">{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consequences Section */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block p-3 bg-[#EF444420] rounded-xl mb-4">
              <AlertTriangle className="w-8 h-8 text-[#EF4444]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              Consequences for <span className="text-[#EF4444]">Violations</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani max-w-3xl mx-auto">
              We take community guidelines seriously. Violations may result in:
            </p>
          </motion.div>

          <div className="space-y-4">
            {consequences.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-xl p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-white font-rajdhani">{item.level}</span>
                  </div>
                  <span 
                    className="font-rajdhani font-bold px-4 py-2 rounded-lg"
                    style={{ backgroundColor: `${item.color}20`, color: item.color }}
                  >
                    {item.action}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 bg-[#1E2338] border border-[#2A2F45] rounded-xl p-6"
          >
            <p className="text-gray-400 font-rajdhani text-center">
              <span className="text-[#FFD700] font-bold">Note:</span> Severe violations like cheating, 
              harassment, or hate speech may result in immediate permanent ban without prior warning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Positive Community Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block px-4 py-2 bg-[#00fff520] border border-[#00fff5] rounded-full mb-6">
                <span className="text-[#00fff5] font-rajdhani font-bold text-sm">BUILDING TOGETHER</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-6">
                A Positive <span className="text-[#00fff5]">Community</span>
              </h2>
              <p className="text-gray-400 font-rajdhani text-lg mb-6 leading-relaxed">
                Every player contributes to our community's atmosphere. By following these guidelines, 
                you help create an environment where everyone can enjoy competitive gaming.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-[#FFD700]" />
                  <span className="text-white font-rajdhani">Be the player you want to play with</span>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-[#FFD700]" />
                  <span className="text-white font-rajdhani">Encourage new players to improve</span>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-[#FFD700]" />
                  <span className="text-white font-rajdhani">Report issues, don't engage with trolls</span>
                </div>
              </div>

              <div className="bg-[#1E2338] border border-[#2A2F45] rounded-xl p-6">
                <p className="text-[#00fff5] font-rajdhani font-bold mb-2">💡 Community Tip</p>
                <p className="text-gray-400">
                  A simple "Good game" or "Nice shot" can make someone's day. Spread positivity!
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-b from-[#1E2338] to-[#14182B] border border-[#2A2F45] rounded-2xl p-8"
            >
              <h3 className="text-2xl font-rajdhani font-bold text-white mb-6">Community Pledge</h3>
              <div className="space-y-4">
                {[
                  'I will treat everyone with respect',
                  'I will compete with integrity',
                  'I will support fellow players',
                  'I will report violations',
                  'I will help keep TheArenaX positive'
                ].map((pledge, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#00E676]" />
                    <span className="text-white font-rajdhani">{pledge}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Report Section */}
      <section className="py-20 px-4 bg-[#0A0F1E]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#00fff510] to-[#7000ff10] border border-[#2A2F45] rounded-3xl p-12 text-center"
          >
            <Flag className="w-16 h-16 text-[#00fff5] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-rajdhani font-bold text-white mb-4">
              See Something? <span className="text-[#00fff5]">Report It</span>
            </h2>
            <p className="text-xl text-gray-400 font-rajdhani mb-8 max-w-2xl mx-auto">
              If you witness any violations of our community guidelines, please report it immediately. 
              All reports are confidential and investigated within 24 hours.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@lastzoneindia.com?subject=Community%20Guidelines%20Violation&body=Please%20describe%20the%20violation%20and%20provide%20any%20evidence:"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition"
              >
                <Mail className="w-5 h-5" />
                Report via Email
              </a>
              
              <Link to="/download">
                <button className="bg-[#1E2338] border border-[#2A2F45] px-8 py-4 rounded-xl text-white font-rajdhani font-bold hover:bg-[#2A2F45] transition flex items-center gap-2">
                  <Flag className="w-5 h-5" />
                  In-App Reporting
                </button>
              </Link>
            </div>

            <p className="text-gray-500 mt-6 text-sm">
              ⚡ Reports are handled by our moderation team within 24 hours
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 font-rajdhani text-sm">
            By participating in TheArenaX, you agree to follow these Community Guidelines. 
            Together, we can build the best gaming community in India.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Guidelines;